import { Link } from "react-router-dom";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, apiError } from "../lib/api";
import AppLogo from "../components/AppLogo";

export default function SignupHospital() {
  const [form, setForm] = useState({
    hospital_name: "", address: "",
    admin_full_name: "", admin_email: "", admin_password: "", admin_phone: "",
  });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/signup/hospital", form);
      setDone(data);
    } catch (err) {
      setError(apiError(err, "Signup failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-mb-bg text-white flex flex-col">
      <header className="px-6 lg:px-12 py-5 border-b border-mb-border">
       <AppLogo
  to="/"
  subtitle="Smart Clinic • Easy Care"
  size={48}
/>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        {done ? (
          <div className="w-full max-w-lg text-center" data-testid="hospital-signup-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-teal-400" />
            <h1 className="mt-6 text-3xl tracking-tight font-light">Submitted for review</h1>
            <p className="mt-3 text-sm text-mb-fg2">
              <span className="text-white font-medium">{done.hospital.name}</span> is in <span className="text-amber-400">pending</span> status. A super admin will approve your account shortly. Once approved, the admin email <span className="text-white">{form.admin_email}</span> can sign in.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/login"><Button className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg" data-testid="hospital-success-signin">Sign in</Button></Link>
              <Link to="/"><Button variant="ghost" className="hover:bg-mb-surface2">Home</Button></Link>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-2xl" data-testid="hospital-signup-card">
            <h1 className="text-3xl tracking-tight font-light">Register your hospital</h1>
            <p className="mt-2 text-sm text-mb-fg2">A super admin will review and activate your account.</p>

            <form onSubmit={onSubmit} className="mt-8 grid md:grid-cols-2 gap-5">
              <Field id="hospital_name" label="Hospital name" value={form.hospital_name} onChange={update("hospital_name")} required testid="h-name" />
              <Field id="address" label="Address" value={form.address} onChange={update("address")} testid="h-address" />
              <div className="md:col-span-2 mt-3 text-xs uppercase tracking-[0.25em] text-mb-fg2">Admin account</div>
              <Field id="admin_full_name" label="Admin full name" value={form.admin_full_name} onChange={update("admin_full_name")} required testid="h-admin-name" />
              <Field id="admin_email" type="email" label="Admin email" value={form.admin_email} onChange={update("admin_email")} required testid="h-admin-email" />
              <Field id="admin_phone" label="Admin phone" value={form.admin_phone} onChange={update("admin_phone")} testid="h-admin-phone" />
              <Field id="admin_password" type="password" label="Admin password" value={form.admin_password} onChange={update("admin_password")} required testid="h-admin-password" />

              {error && <div data-testid="hospital-signup-error" className="md:col-span-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}
              <div className="md:col-span-2 flex gap-3">
                <Button type="submit" disabled={submitting} data-testid="hospital-signup-submit"
                  className="bg-teal-500 hover:bg-teal-400 text-zinc-950 h-11 px-6 rounded-lg font-medium">
                  {submitting ? "Submitting…" : "Submit for review"}
                </Button>
                <Link to="/login"><Button variant="ghost" type="button" className="h-11 hover:bg-mb-surface2">Sign in instead</Button></Link>
              </div>
            </form>
          </div>
        )}
      </main>
      <footer className="border-t border-mb-border px-6 py-5 text-center text-xs text-mb-fg2">
  <div>© 2026 MediBooks</div>
  <div className="mt-1">A Product of MNV Solutions</div>
</footer>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required, testid }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input id={id} type={type} value={value} onChange={onChange} required={required}
        data-testid={testid}
        className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg focus-visible:ring-teal-500" />
    </div>
  );
}
